import React from 'react';
import Image from 'next/image';
import { PageError } from '~/components/page-errors';
import CustomList, { Item } from '~/components/product-renewal/custom-list';
import sanitizeHtml, { defaultSanitizeConfig } from '~/utils/sanitize-html';
import {
    CostContainer,
    CostDeliveryContainer,
    CreditCardContainer,
    CreditCardSection,
    DetailItem,
    PayCardImageContainer,
} from '~/components/product-renewal/styles';
import { ERROR_CODE_PARAM, LOCALE_PARAM, SOURCE_PARAM, STATUS_CODE_QUERY_PARAM } from '~/constants';
import { SET_RENEWAL_FLOW } from '~/store/actions';
import { ProductRenewalType, RenewalFlow, StoreStateProps } from '~/store/types';
import {
    SET_DL_RENEWAL_ELIGIBILITY_DATA,
    SET_HC_RENEWAL_ELIGIBILITY_DATA,
    SET_OPC_RENEWAL_ELIGIBILITY_DATA,
} from '~/store/actions';
import { Heading3, Heading6 } from '~/theme/headings';
import { BoldText, UnorderedList } from '~/theme/elements';
import { TextParagraph } from '~/theme';
import { imageUrl } from '~/utils/url-helpers';
import { unFormat } from '~/utils/helpers';
import {
    DlRenewalEligibilityCheckRequest,
    DlRenewalEligibilityCheckResponse,
    HcRenewalEligibilityCheckRequest,
    HcRenewalEligibilityCheckResponse,
    OpcRenewalEligibilityCheckRequest,
    OpcRenewalEligibilityCheckResponse,
    TransactionCategory,
    RenewalEligibilityData,
} from '~/models/product-renewal';
import productRenewalService from '~/services/product-renewal.service';

type TranslationFunction = (key: string, options?: Record<string, unknown>) => unknown;

type RenewalFieldValues = Record<string, string | null | undefined>;

type NormalizedRenewalFieldValues<T extends RenewalFieldValues> = {
    [K in keyof T]: string;
};

type RenewalStepConfig = {
    default: number;
    DL?: number;
    DLHC?: number;
    HC?: number;
    OPC?: number;
};

type RenewalRouteMap = {
    default?: string;
    DL?: string;
    DLHC?: string;
    HC?: string;
    OPC?: string;
};

type FeedbackTranslationFunction = (key: string, options?: Record<string, unknown>) => string;

type ProductRenewalFeedbackUrlOptions = {
    translate: FeedbackTranslationFunction;
    renewalType?: ProductRenewalType;
    isProd: boolean;
    isGuestUser: boolean;
    statusCode: 100 | 400;
    errorCodes?: string | string[];
    locale?: string;
    source?: string;
    transactionId?: string;
};

type TranslatedSectionContent = {
    title?: string;
    items?: Array<string | Item>;
    summaryItems?: Array<{
        label: string;
        value: string;
    }>;
    paymentMethod?: {
        title: string;
        value: string;
        showCardIcons?: boolean;
    };
    feeVariation?: {
        title: string;
        items: string[];
    };
};

type TranslatedSectionGroup = TranslatedSectionContent[];

type IntendedFlowDispatch = (action: {
    type: string;
    payload: {
        intendedFlow?: string;
        renewalFlow?: RenewalFlow;
    };
}) => void;

const DEFAULT_VERIFICATION_FLOW_BY_RENEWAL_TYPE: Record<ProductRenewalType, RenewalFlow> = {
    DL: 'DLV',
    OPC: 'OPCV',
    HC: 'HCV',
    DLHC: 'DLHCV',
};

export const mapErrorCodesToPageErrors = (errorCodes?: string[]): PageError[] =>
    (errorCodes ?? []).map(errorCode => ({ errorCode }));

export const buildPageErrorState = (
    ...errorGroups: Array<PageError[] | undefined>
): PageError[] | PageError | undefined => {
    const errors = errorGroups.reduce<PageError[]>((result, group) => {
        if (group?.length) {
            result.push(...group);
        }

        return result;
    }, []);

    if (!errors.length) {
        return undefined;
    }

    return errors.length > 1 ? errors : errors[0];
};

export const buildTranslatedItems = (
    translate: TranslationFunction,
    key: string,
    options: Record<string, unknown> = {}
): Item[] => [...((translate(key, { returnObjects: true, ...options }) as Item[]) ?? [])];

export const buildTranslatedTextItems = (
    translate: TranslationFunction,
    key: string,
    options: Record<string, unknown> = {}
): Item[] =>
    buildTranslatedItems(translate, key, options).map(item => ({
        text: typeof item === 'string' ? item : item.text,
    }));

const hasHtmlTag = (value: string): boolean => /<\/?[a-z][\s\S]*>/i.test(value);

const stripHtmlTags = (value: string): string => value.replace(/<[^>]+>/g, '').trim();

const isDlhcEligibilityHeadingTag = (value: string): boolean =>
    /^<subheading>.*<\/subheading>$/.test(value.trim());

const normalizeTranslatedSectionItems = (items: Array<string | Item> = []): Item[] =>
    items.map(item => {
        if (typeof item === 'string') {
            return {
                text: item,
                html: hasHtmlTag(item),
            };
        }

        return {
            ...item,
            html: item.html ?? hasHtmlTag(item.text),
        };
    });

export const renderTranslatedRenewalSection = ({
    translate,
    key,
    renewalType,
    options = {},
    hideTitle = false,
}: {
    translate: TranslationFunction;
    key: string;
    renewalType?: ProductRenewalType;
    options?: Record<string, unknown>;
    hideTitle?: boolean;
}) => {
    if (!renewalType) {
        return null;
    }

    const section =
        (translate(`${key}.${renewalType}`, {
            returnObjects: true,
            ...options,
        }) as TranslatedSectionContent | TranslatedSectionGroup) ?? {};

    if (Array.isArray(section)) {
        return (
            <>
                {section.map((sectionItem, index) => {
                    const sectionItems = normalizeTranslatedSectionItems(sectionItem.items);
                    const titleHasHtml = sectionItem.title && hasHtmlTag(sectionItem.title);

                    if (!sectionItem.title && !sectionItems.length) {
                        return null;
                    }

                    return (
                        <React.Fragment key={`translated-section-${index}`}>
                            {sectionItem.title &&
                                !hideTitle &&
                                (titleHasHtml ? (
                                    <TextParagraph
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeHtml(
                                                sectionItem.title,
                                                defaultSanitizeConfig
                                            ),
                                        }}
                                    />
                                ) : (
                                    <TextParagraph>{sectionItem.title}</TextParagraph>
                                ))}
                            {!!sectionItems.length && (
                                <CustomList haveHeader={false} heading="" items={sectionItems} />
                            )}
                        </React.Fragment>
                    );
                })}
            </>
        );
    }

    if (section.summaryItems || section.paymentMethod || section.feeVariation) {
        return (
            <>
                {section.title && !hideTitle && <TextParagraph>{section.title}</TextParagraph>}

                {!!section.summaryItems?.length && (
                    <CostContainer $columns={Math.min(section.summaryItems.length, 3)}>
                        {section.summaryItems.map((item, index) => (
                            <DetailItem key={`cost-summary-${index}`}>
                                <BoldText>{item.label}</BoldText>
                                <TextParagraph>{item.value}</TextParagraph>
                            </DetailItem>
                        ))}
                    </CostContainer>
                )}

                {section.paymentMethod && (
                    <CreditCardSection>
                        <Heading6>{section.paymentMethod.title}</Heading6>
                        <CreditCardContainer>
                            <TextParagraph>{section.paymentMethod.value}</TextParagraph>
                            {section.paymentMethod.showCardIcons && (
                                <PayCardImageContainer>
                                    <Image
                                        aria-hidden="true"
                                        src={imageUrl('images/ontario-icon-mastercard.svg')}
                                        alt="MasterCard"
                                        width={24}
                                        height={24}
                                    />
                                    <Image
                                        aria-hidden="true"
                                        src={imageUrl('images/ontario-icon-visa.svg')}
                                        alt="Visa"
                                        width={24}
                                        height={24}
                                    />
                                </PayCardImageContainer>
                            )}
                        </CreditCardContainer>
                    </CreditCardSection>
                )}

                {section.feeVariation && (
                    <>
                        <Heading6>{section.feeVariation.title}</Heading6>
                        <CostDeliveryContainer>
                            <UnorderedList>
                                {section.feeVariation.items.map((item, index) => (
                                    <li key={`cost-fee-variation-${index}`}>{item}</li>
                                ))}
                            </UnorderedList>
                        </CostDeliveryContainer>
                    </>
                )}
            </>
        );
    }

    const items = normalizeTranslatedSectionItems(section.items);

    if (!section.title && !items.length) {
        return null;
    }

    const isDlhcEligibilitySection = key.endsWith('.eligibilityDetails') && renewalType === 'DLHC';

    if (isDlhcEligibilitySection) {
        const sections = items.reduce<Array<{ heading: string; items: Item[] }>>((result, item) => {
            if (item.html && isDlhcEligibilityHeadingTag(item.text)) {
                result.push({
                    heading: stripHtmlTags(item.text),
                    items: [],
                });

                return result;
            }

            const currentSection = result[result.length - 1];

            if (currentSection) {
                currentSection.items.push(item);
            }

            return result;
        }, []);

        return (
            <>
                {section.title && !hideTitle && (
                    <TextParagraph>
                        <Heading3>{section.title}</Heading3>
                    </TextParagraph>
                )}
                {sections.map((group, index) => (
                    <React.Fragment key={`dlhc-eligibility-group-${index}`}>
                        <Heading6>{group.heading}</Heading6>
                        {!!group.items.length && (
                            <CustomList haveHeader={false} heading="" items={group.items} />
                        )}
                    </React.Fragment>
                ))}
            </>
        );
    }

    return (
        <>
            {section.title && !hideTitle && <TextParagraph>{section.title}</TextParagraph>}
            {!!items.length && <CustomList haveHeader={false} heading="" items={items} />}
        </>
    );
};

export const unformatRenewalFields = <T extends RenewalFieldValues>(
    values: T
): NormalizedRenewalFieldValues<T> =>
    (Object.keys(values) as Array<keyof T>).reduce((result, key) => {
        const value = values[key];

        result[key] = (unFormat(value ?? undefined) ??
            value ??
            '') as NormalizedRenewalFieldValues<T>[keyof T];

        return result;
    }, {} as NormalizedRenewalFieldValues<T>);

export const getRenewalTotalSteps = (renewalType?: ProductRenewalType): number => {
    switch (renewalType) {
        case 'DLHC':
            return 6;
        case 'DL':
            return 4;
        case 'OPC':
            return 4;
        case 'HC':
            return 5;
        default:
            return 5;
    }
};

export const getRenewalCurrentStep = (
    renewalType: ProductRenewalType | undefined,
    config: RenewalStepConfig
): number => {
    if (renewalType === 'DL' && config.DL !== undefined) {
        return config.DL;
    }

    if (renewalType === 'DLHC' && config.DLHC !== undefined) {
        return config.DLHC;
    }

    if (renewalType === 'HC' && config.HC !== undefined) {
        return config.HC;
    }

    if (renewalType === 'OPC' && config.OPC !== undefined) {
        return config.OPC;
    }

    return config.default;
};

export const getRenewalRouteByType = (
    renewalType: ProductRenewalType | undefined,
    routes: RenewalRouteMap
): string => {
    if (renewalType === 'DL' && routes.DL) {
        return routes.DL;
    }

    if (renewalType === 'DLHC' && routes.DLHC) {
        return routes.DLHC;
    }

    if (renewalType === 'HC' && routes.HC) {
        return routes.HC;
    }

    if (renewalType === 'OPC' && routes.OPC) {
        return routes.OPC;
    }

    return routes.default ?? '';
};

export const setRenewalFlowByRenewalType = (
    renewalType: ProductRenewalType | undefined,
    options: {
        flowByRenewalType?: Partial<Record<ProductRenewalType, RenewalFlow>>;
        fallbackFlow?: RenewalFlow;
        dispatch?: IntendedFlowDispatch;
    } = {}
): RenewalFlow => {
    const {
        flowByRenewalType = DEFAULT_VERIFICATION_FLOW_BY_RENEWAL_TYPE,
        fallbackFlow = DEFAULT_VERIFICATION_FLOW_BY_RENEWAL_TYPE.DLHC,
        dispatch,
    } = options;

    const renewalFlow = renewalType
        ? (flowByRenewalType[renewalType] ?? fallbackFlow)
        : fallbackFlow;

    if (dispatch) {
        dispatch({
            type: SET_RENEWAL_FLOW,
            payload: {
                renewalFlow,
            },
        });
    }

    return renewalFlow;
};

export const getProductRenewalFeedbackKey = (
    renewalType?: ProductRenewalType
): 'DLHC' | 'invalidDL' | 'invalidHC' | 'OPC' => {
    switch (renewalType) {
        case 'DL':
            return 'invalidDL';
        case 'HC':
            return 'invalidHC';
        case 'OPC':
            return 'OPC';
        default:
            return 'DLHC';
    }
};

export const getProductRenewalFeedbackBaseUrl = ({
    translate,
    renewalType,
    isProd,
    isGuestUser,
}: {
    translate: FeedbackTranslationFunction;
    renewalType?: ProductRenewalType;
    isProd: boolean;
    isGuestUser: boolean;
}): string => {
    const feedbackKey = getProductRenewalFeedbackKey(renewalType);
    const environmentKey = isProd ? 'production' : 'uat';
    const userTypeKey = isGuestUser ? 'guest' : 'user';
    const structuredUrl =
        translate(
            `product-renewal:inEligible.feedbackUrls.${feedbackKey}.${environmentKey}.${userTypeKey}`
        ) ?? '';

    if (structuredUrl) {
        return structuredUrl;
    }

    const guestKey = isGuestUser ? 'Guest' : '';
    return (
        translate(
            `product-renewal:inEligible.errors.${feedbackKey}.${environmentKey}FeedbackUrl${guestKey}`
        ) ?? ''
    );
};

export const buildProductRenewalFeedbackUrl = ({
    translate,
    renewalType,
    isProd,
    isGuestUser,
    statusCode,
    errorCodes,
    locale,
    source,
    transactionId,
}: ProductRenewalFeedbackUrlOptions): string => {
    const feedbackBaseUrl = getProductRenewalFeedbackBaseUrl({
        translate,
        renewalType,
        isProd,
        isGuestUser,
    });

    const queryParts = [STATUS_CODE_QUERY_PARAM[statusCode]];

    if (errorCodes) {
        const formattedCodes = Array.isArray(errorCodes)
            ? errorCodes.filter(Boolean).join(',')
            : errorCodes;

        if (formattedCodes) {
            queryParts.push(`${ERROR_CODE_PARAM}${formattedCodes}`);
        }
    }

    if (source) {
        queryParts.push(`${SOURCE_PARAM}${source}`);
    }

    if (locale) {
        queryParts.push(`${LOCALE_PARAM}${locale}`);
    }

    if (transactionId) {
        queryParts.push(`&tid=${transactionId}`);
    }

    return `${feedbackBaseUrl}${queryParts.join('')}`;
};

export const setTypeAndCardDefault = (
    queryType: string | undefined,
    queryCard: string | undefined,
    state: StoreStateProps
): { type: string; card: string } => {
    const hasDriversLicenceDetails = !!state?.productRenewal?.driversLicenceDetails;
    const hasPhotoCardDetails = !!state?.productRenewal?.photoCardDetails;
    const type = !!queryType
        ? queryType.toString()
        : hasDriversLicenceDetails
          ? 'DriverLicense'
          : hasPhotoCardDetails
            ? 'PhotoCard'
            : '';
    const card = !!queryCard
        ? queryCard.toString()
        : hasDriversLicenceDetails
          ? (state?.productRenewal?.driversLicenceDetails?.driversLicenceNumber ?? '')
          : hasPhotoCardDetails
            ? (state?.productRenewal?.photoCardDetails?.photoCardNumber ?? '')
            : '';

    return { type, card };
};

export const getTransactionCategories = (
    renewalType: ProductRenewalType | undefined,
    eligibilityData?: RenewalEligibilityData,
    hasDriverLicense = false,
    hasPhotoCard = false
): TransactionCategory[] => {
    switch (renewalType) {
        case 'HC': {
            const transactionCategories: TransactionCategory[] = [];

            if (eligibilityData?.driverLicenseEligibility || hasDriverLicense) {
                transactionCategories.push('RenewHCWithDL');
            } else if (eligibilityData?.photoCardEligibility || hasPhotoCard) {
                transactionCategories.push('RenewHCWithOPC');
            }

            transactionCategories.push('RenewHC');

            return transactionCategories;
        }
        case 'DL':
            return ['RenewDL'];
        case 'OPC':
            return ['RenewOPC'];
        case 'DLHC':
            return ['RenewHCandDL', 'RenewHC'];
        default:
            return [];
    }
};

export const hasHardStopEligibilityError = (
    currentEligibilityData?: RenewalEligibilityData,
    renewalFlow?: RenewalFlow
): boolean => {
    const activeFlow = renewalFlow || '';
    const includeDriverLicense = ['DLV', 'DLHCV'].includes(activeFlow);
    const includePhotoCard = ['OPCV'].includes(activeFlow);
    const includeHealthCard = ['HCV', 'DLHCV'].includes(activeFlow);
    const includeEResidency = ['HCV', 'DLHCV'].includes(activeFlow);

    const eligibilityEntries = [
        ...(includeDriverLicense && currentEligibilityData?.driverLicenseEligibility != null
            ? [currentEligibilityData.driverLicenseEligibility]
            : []),
        ...(includePhotoCard && currentEligibilityData?.photoCardEligibility != null
            ? [currentEligibilityData.photoCardEligibility]
            : []),
        ...(includeHealthCard && currentEligibilityData?.healthCardEligibility != null
            ? [currentEligibilityData.healthCardEligibility]
            : []),
        ...(includeEResidency && currentEligibilityData?.eResidencyCheck != null
            ? [currentEligibilityData.eResidencyCheck]
            : []),
    ];

    return eligibilityEntries.some(entry => {
        if (!entry) {
            return false;
        }

        const hasCxpErrorCode =
            entry.cxpErrorCode != null &&
            typeof entry.cxpErrorCode === 'string' &&
            entry.cxpErrorCode.trim() !== '';

        return hasCxpErrorCode || entry.success === false;
    });
};

type EligibilityDispatch = (action: {
    type: string;
    payload: {
        productRenewal: {
            eligibilityData: RenewalEligibilityData;
            transactionCategory: TransactionCategory;
        };
    };
}) => void;

export type EligibilityRequests = {
    dl?: DlRenewalEligibilityCheckRequest;
    opc?: OpcRenewalEligibilityCheckRequest;
    hc?: HcRenewalEligibilityCheckRequest;
};

export const applyEligibilityResponse = async (
    transactionCategory: TransactionCategory,
    dispatch: EligibilityDispatch,
    requests: EligibilityRequests,
    eligibilityData?: RenewalEligibilityData
): Promise<RenewalEligibilityData> => {
    let eligibilityResponse;

    if (['RenewDL', 'RenewHCWithDL', 'RenewHCandDL'].includes(transactionCategory)) {
        if (!requests.dl) {
            throw new Error(
                `DL eligibility request is required for transactionCategory: ${transactionCategory}`
            );
        }
        eligibilityResponse = await productRenewalService.checkEligibilityForDL({
            ...requests.dl,
            transactionCategory,
        });
    } else if (['RenewOPC', 'RenewHCWithOPC', 'RenewHCandOPC'].includes(transactionCategory)) {
        if (!requests.opc) {
            throw new Error(
                `OPC eligibility request is required for transactionCategory: ${transactionCategory}`
            );
        }
        eligibilityResponse = await productRenewalService.checkEligibilityForOPC({
            ...requests.opc,
            transactionCategory,
        });
    } else if (['RenewHC'].includes(transactionCategory)) {
        if (!requests.hc) {
            throw new Error(
                `HC eligibility request is required for transactionCategory: ${transactionCategory}`
            );
        }
        const { driverLicenseEligibility, photoCardEligibility } = eligibilityData ?? {};
        const { cxpTransactionId } = driverLicenseEligibility ?? photoCardEligibility ?? {};
        eligibilityResponse = await productRenewalService.checkEligibilityForHC({
            ...requests.hc,
            cxpTransactionId: cxpTransactionId ?? requests.hc.cxpTransactionId,
        });
    }

    const {
        eResidencyCheck,
        driverLicenseEligibility,
        photoCardEligibility,
        healthCardEligibility,
    } = (eligibilityResponse?.data ?? {}) as Partial<
        DlRenewalEligibilityCheckResponse &
            OpcRenewalEligibilityCheckResponse &
            HcRenewalEligibilityCheckResponse
    >;

    if (driverLicenseEligibility) {
        dispatch({
            type: SET_DL_RENEWAL_ELIGIBILITY_DATA,
            payload: {
                productRenewal: {
                    eligibilityData: {
                        eResidencyCheck,
                        driverLicenseEligibility,
                    },
                    transactionCategory,
                },
            },
        });
    }

    if (photoCardEligibility) {
        dispatch({
            type: SET_OPC_RENEWAL_ELIGIBILITY_DATA,
            payload: {
                productRenewal: {
                    eligibilityData: {
                        eResidencyCheck,
                        photoCardEligibility,
                    },
                    transactionCategory,
                },
            },
        });
    }

    if (healthCardEligibility) {
        dispatch({
            type: SET_HC_RENEWAL_ELIGIBILITY_DATA,
            payload: {
                productRenewal: {
                    eligibilityData: {
                        healthCardEligibility,
                    },
                    transactionCategory,
                },
            },
        });
    }

    return {
        eResidencyCheck,
        driverLicenseEligibility,
        photoCardEligibility,
        healthCardEligibility,
    };
};

export const buildEligibilityResult = async (
    renewalType: ProductRenewalType | undefined,
    eligibilityData: RenewalEligibilityData | undefined,
    dispatch: EligibilityDispatch,
    requests: EligibilityRequests,
    hasDriverLicense = false,
    hasPhotoCard = false
): Promise<RenewalEligibilityData> => {
    const transactionCategories = getTransactionCategories(
        renewalType,
        eligibilityData,
        hasDriverLicense,
        hasPhotoCard
    );

    if (!renewalType || !transactionCategories.length) {
        throw new Error('Unable to determine renewal eligibility transaction category');
    }

    let accumulated: RenewalEligibilityData = { ...(eligibilityData ?? {}) };

    for (const transactionCategory of transactionCategories) {
        const result = await applyEligibilityResponse(
            transactionCategory,
            dispatch,
            requests,
            accumulated
        );
        accumulated = { ...accumulated, ...result };
    }

    return accumulated;
};
